import { useState, useCallback } from 'react'
import { useContent } from '../../contexts/ContentContext'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import ScrollableSection from '../ui/ScrollableSection'
import SectionLabel from '../ui/SectionLabel'
import LazyImage from '../ui/LazyImage'
import ExpandToggleButton from '../ui/ExpandToggleButton'

/**
 * ResearcherQuotesSection — Research authority quotes with composite typography utilities
 *
 * MIGRATED to composite typography utilities:
 * - H2-on-dark: typography-h2-on-dark (replaces typography-h2 text-primary-on-dark)
 * - Quote-featured-on-dark: typography-quote-featured-on-dark (replaces typography-quote-featured text-primary-on-dark)
 * - Author-name-on-dark: typography-author-name-on-dark (replaces typography-author-name text-primary-on-dark)
 * - Meta-on-dark: typography-meta-on-dark (replaces typography-meta text-secondary-on-dark)
 *
 * REDESIGNED with cleaner spacing pattern:
 * - Label → H2 → Grid structure
 * - Wrapper div controls spacing between header group and grid
 * - Uses design tokens, no hardcoded values
 *
 * FEATURES:
 * - Filter for authors with portraits
 * - Expandable "Mehr/Weniger" for additional authors
 * - Responsive: 16:9 on mobile, 1:1 on desktop
 */
export default function ResearcherQuotesSection() {
  const { researchers, ui } = useContent()
  const [isExpanded, setIsExpanded] = useState(false)

  // Simple scroll to align section top when expanding
  const handleToggle = useCallback(() => {
    const willExpand = !isExpanded
    setIsExpanded(willExpand)

    // When opening, scroll to first newly visible author (4th author overall)
    if (willExpand) {
      requestAnimationFrame(() => {
        // Target the expanded grid (contains authors 4+)
        const expandedGrid = document.querySelector('[data-testid="researcher-quotes-expanded"]')
        if (expandedGrid) {
          const rect = expandedGrid.getBoundingClientRect()
          const scrollTop = window.scrollY + rect.top - 100

          window.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          })
        }
      })
    }
  }, [isExpanded])

  // Filter authors with portraits; allow per-author opt-out via showInQuotes: false
  const authorsWithPortraits = researchers.authors.filter(
    author => author.portrait !== null && author.showInQuotes !== false
  )
  const hasMore = authorsWithPortraits.length > 3

  return (
    <ScrollableSection hasAccordion={true} className="bg-color-primary text-primary-on-dark" data-testid="researcher-quotes-section">
      {/* Header group with controlled spacing */}
      <div className="margin-top-md">
        <SectionLabel text={researchers.authorBigLabel} light />
        <h2 className="typography-h2-mobile-on-dark md:typography-h2-on-dark leading-tight heading-body-spacing">
          {researchers.authorHeadline}
        </h2>
      </div>

      {/* Grid - first 3 authors */}
      <div className="grid md:grid-cols-3 author-grid-gap md:author-grid-gap-lg">
        {authorsWithPortraits.slice(0, 3).map((author) => (
          <div key={author.id}>
            {/* Mobile: 16:9 aspect ratio */}
            <div className="md:hidden mb-4">
              <AspectRatio ratio={16 / 9}>
                <LazyImage
                  src={author.portrait}
                  alt={author.name}
                  className="w-full h-full object-cover rounded-lg"
                  objectPosition={author.portraitFocal ? `${author.portraitFocal.x}% ${author.portraitFocal.y}%` : 'center'}
                />
              </AspectRatio>
            </div>

            {/* Desktop: 1:1 aspect ratio */}
            <div className="hidden md:block mb-4">
              <AspectRatio ratio={1 / 1}>
                <LazyImage
                  src={author.portrait}
                  alt={author.name}
                  className="w-full h-full object-cover rounded-lg"
                  objectPosition={author.portraitFocal ? `${author.portraitFocal.x}% ${author.portraitFocal.y}%` : 'center'}
                />
              </AspectRatio>
            </div>

            {/* Quote */}
            <p className="typography-quote-featured-on-dark content-spacing">
              "{author.quote}"
            </p>

            {/* Name */}
            <p className="typography-author-name-on-dark">
              {author.name}
            </p>

            {/* Role */}
            <p className="typography-label-on-dark mt-1">
              {author.role}
            </p>
          </div>
        ))}
      </div>

      {/* Expandable "Mehr/Weniger" - only show if more than 3 authors */}
      {hasMore && (
        <>
          <ExpandToggleButton
            isExpanded={isExpanded}
            onToggle={handleToggle}
            labelMore={ui.showMore}
            labelLess={ui.showLess}
            className="my-10"
          />

          {/* Expanded content - authors 4+ */}
          {isExpanded && (
            <div className="grid md:grid-cols-3 author-grid-gap md:author-grid-gap-lg" data-testid="researcher-quotes-expanded">
              {authorsWithPortraits.slice(3).map((author) => (
                <div key={author.id}>
                  <div className="md:hidden mb-4">
                    <AspectRatio ratio={16 / 9}>
                      <LazyImage
                        src={author.portrait}
                        alt={author.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </AspectRatio>
                  </div>

                  <div className="hidden md:block mb-4">
                    <AspectRatio ratio={1 / 1}>
                      <LazyImage
                        src={author.portrait}
                        alt={author.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </AspectRatio>
                  </div>

                  <p className="typography-quote-featured-on-dark content-spacing">
                    "{author.quote}"
                  </p>

                  <p className="typography-author-name-on-dark">
                    {author.name}
                  </p>

                  <p className="typography-meta-on-dark mt-1">
                    {author.role}
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </ScrollableSection>
  )
}
